import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import {
  MAXIMUM_ALLOWED_SAMPLES_COUNT,
  MAXIMUM_PATH_LENGTH,
  OperatingSystem,
  SubmitToSandboxSamplesFormWebComponentSchema,
} from '../types';
import { GzTitleWebComponent } from '../../../../../ui/gzTitle';
import { GzTextWebComponent } from '../../../../../ui/gzText';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzInputTextGridWebComponent } from '../../../../../ui/gzInputTextGrid';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { FormValidator, ValidationResult } from '../../../../../ui/gzForm/formField/types/form-validator';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';

export class SubmitToSandboxSamplesFormView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $title: GzTitleWebComponent;
    $description: GzTextWebComponent;
    $inputGridFormField: GzFormFieldWebComponent;
    $inputGrid: GzInputTextGridWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#wrapper'),
      $title: this.shadow.querySelector('#title'),
      $description: this.shadow.querySelector('#description'),
      $inputGridFormField: this.shadow.querySelector('#input-text-grid-form-field'),
      $inputGrid: this.shadow.querySelector('#input-grid'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$title.innerHTML = translateFn('Samples');

    this.dom.$description.innerHTML = translateFn(
      'Specify the full path to the sample you want detonated. For example, <strong>D:\\temp\\test.exe</strong>.<br>You can add up to 5 samples per submission. Each sample must be up to 100 MB.'
    );

    this.dom.$inputGrid.label = translateFn('Add path');
    this.dom.$inputGrid.placeholder = translateFn('Specify the full path');
    this.dom.$inputGrid.columnName = translateFn('Path');
    this.dom.$inputGrid.emptyStateButtonLinkText = translateFn('Add path');
  }

  buildView(data: SubmitToSandboxSamplesFormWebComponentSchema) {
    this.dom.$inputGrid.required = true;
    this.dom.$inputGrid.duplicateErrorMessage = translateFn('The path already exists.');
    this.dom.$inputGrid.maxItems = MAXIMUM_ALLOWED_SAMPLES_COUNT;
    this.dom.$inputGrid.maxItemsReachedErrorMessage = translateFn('The limit of {maxPaths} paths has been reached.', {
      maxPaths: MAXIMUM_ALLOWED_SAMPLES_COUNT,
    });

    // There must be at least one path stored in the grid. This validation will only enable/disable the "Save" button
    this.dom.$inputGridFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$inputGrid.InputValidators = [
      MaxLengthValidator(
        MAXIMUM_PATH_LENGTH,
        translateFn('The path must have a maximum of {maxLength} characters.', {
          maxLength: MAXIMUM_PATH_LENGTH,
        })
      ),
      this.getPathRegexValidator(),
    ];
  }

  private getPathRegexValidator(): FormValidator {
    const operatingSystem = this.dependencies.getTargetOperatingSystem();

    if (operatingSystem === OperatingSystem.Windows) {
      const windowsPathRegex: RegExp = /^([a-zA-Z]:)(\\[^ \\/:*?"<>|]+([ ]+[^ \\/:*?"<>|]+)*)+$/;
      return RegexValidator(windowsPathRegex, translateFn('Invalid format'));
    }

    return CustomizableValidator(this.UnixRegexValidatorFunction.bind(this));
  }

  private UnixRegexValidatorFunction(value: string): ValidationResult {
    if (!value || !/(\0|^[^\/]|^\/*$|\/$)/.test(value)) {
      return { valid: true };
    }

    return { valid: false, error: translateFn('Invalid format') };
  }
}
